import { DEFAULT_OFFLINE_WAIT_CEILING_MS, waitForOnline } from './network-online';
import { RetryOptions } from './retry';
import { isAbortError, toError } from './toError';

/**
 * Additional retries after the first attempt (total attempts = 1 + this). Kept
 * low because offline stretches are parked (see waitForOnline) rather than
 * burning attempts — these retries only cover *online* transient errors.
 */
export const UPLOAD_REQUEST_MAX_RETRIES = 2;
/** First retry waits this long; each subsequent retry multiplies by the factor below. */
export const UPLOAD_REQUEST_RETRY_INITIAL_DELAY_MS = 500;
export const UPLOAD_REQUEST_BACKOFF_MULTIPLIER = 2;
/**
 * Hard deadline for a single handshake request (start / finish). These are tiny
 * JSON calls that complete in well under a second on any working link, so this
 * only ever trips on a request that returns *nothing* (e.g. a black-hole /
 * 100%-loss connection where `navigator.onLine` still reports true, so the
 * offline park never engages). A timeout surfaces as a retryable TimeoutError,
 * so a hung start no longer sticks the file at "Queued" forever — it retries
 * and, if still dead, fails cleanly.
 */
export const UPLOAD_REQUEST_TIMEOUT_MS = 30_000;

/**
 * A signal that aborts when `signal` aborts OR after `ms`, whichever comes first.
 * A user cancel propagates the original reason (`AbortError` → not retried); the
 * timeout aborts with a `TimeoutError` (retried — see `isConnectivityFailure`).
 * Build one PER attempt so each retry gets a fresh deadline.
 *
 * Hand-rolled with AbortController + setTimeout deliberately: `AbortSignal.any`
 * lands only in Safari 17.4+ / Chrome 116+, and this app has no polyfill or
 * browserslist floor — leaning on it would throw on older mobile Safari and fail
 * every upload there. The cost of the manual version is benign: on a SUCCESSFUL
 * request the timer runs out `ms` and fires a no-op abort on the already-settled
 * controller before its cleanup runs — bounded and self-clearing (a far better
 * trade than a broken upload).
 */
export function signalWithTimeout(signal: AbortSignal | undefined, ms: number): AbortSignal {
  const controller = new AbortController();
  const timer = setTimeout(
    () => controller.abort(new DOMException(`Request timed out after ${ms}ms`, 'TimeoutError')),
    ms,
  );
  const onCancel = () => controller.abort(signal?.reason);
  const cleanup = () => {
    clearTimeout(timer);
    signal?.removeEventListener('abort', onCancel);
  };
  controller.signal.addEventListener('abort', cleanup, { once: true });
  if (signal) {
    if (signal.aborted) controller.abort(signal.reason);
    else signal.addEventListener('abort', onCancel, { once: true });
  }
  return controller.signal;
}

/** Deterministic 402 billing failures never change between attempts. */
function isBillingError(error: unknown): boolean {
  return (
    !!error &&
    typeof error === 'object' &&
    'status' in error &&
    (error as { status?: number }).status === 402
  );
}

export interface UploadRetryOptions<T> {
  /** Aborts both the offline wait and (if the op honors it) the request. */
  signal?: AbortSignal;
  /** Fires once when an attempt starts parking because we are offline. */
  onWait?: () => void;
  /** Per-retry hook (attempt #, delay ms, error) — for logging. */
  onRetry?: RetryOptions['onRetry'];
  /** Override the offline park ceiling for this call. */
  offlineWaitCeilingMs?: number;
  /** Override the retry count for this call. */
  maxRetries?: number;
  /** First-retry delay for this call (default UPLOAD_REQUEST_RETRY_INITIAL_DELAY_MS). */
  initialDelayMs?: number;
  /**
   * Ceiling on the exponential backoff for this call. Default: uncapped, which
   * is harmless for the 3-attempt default but matters for long budgets — eight
   * uncapped doublings from 1s would wait over two minutes on the last retry.
   */
  maxDelayMs?: number;
  /**
   * Read-after-error recovery for **non-idempotent** operations. Called after an
   * attempt errors (before backing off) to check whether the operation actually
   * landed server-side despite the client-side error (lost response / unlogged
   * pod). Returning true stops the retry loop and resolves as success —
   * **`retryUploadRequest` then resolves with `undefined`**, so only use this for
   * operations whose return value you don't consume (e.g. finish). Errors thrown
   * by the check itself are swallowed (treated as "not confirmed").
   */
  verifySucceeded?: () => Promise<boolean>;
  /**
   * Errors that will not improve with more attempts (e.g. a deterministic dedup
   * conflict). Returning true stops retrying immediately and rethrows — unless
   * `verifySucceeded` already recovered it. AbortError and 402 are always
   * treated this way and need not be listed here.
   */
  isDeterministicFailure?: (error: unknown) => boolean;
}

/**
 * Standard resilience wrapper for every network step of an upload. Before each
 * attempt it parks while offline (so a dead radio doesn't burn the retry
 * budget), then retries *online* failures with exponential backoff.
 *
 * Failure handling is pluggable so each of the (idempotent and non-idempotent)
 * call sites can avoid its own pitfall:
 * - idempotent ops (chunk PUT, GET, DELETE) pass nothing → plain retry.
 * - non-idempotent ops that can be state-checked (finish) pass `verifySucceeded`
 *   so a landed-but-errored attempt is recovered instead of re-fired/failed.
 * - non-idempotent ops with a deterministic conflict (start/external) pass
 *   `isDeterministicFailure` so a conflict bails fast instead of retry-looping.
 */
export async function retryUploadRequest<T>(
  operation: () => Promise<T>,
  options: UploadRetryOptions<T> = {},
): Promise<T> {
  const {
    signal,
    onWait,
    onRetry,
    offlineWaitCeilingMs = DEFAULT_OFFLINE_WAIT_CEILING_MS,
    maxRetries = UPLOAD_REQUEST_MAX_RETRIES,
    initialDelayMs = UPLOAD_REQUEST_RETRY_INITIAL_DELAY_MS,
    maxDelayMs = Infinity,
    verifySucceeded,
    isDeterministicFailure,
  } = options;

  let lastError: unknown;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    // Park until online (or the ceiling) before spending an attempt.
    await waitForOnline(offlineWaitCeilingMs, signal, onWait);
    try {
      return await operation();
    } catch (error) {
      lastError = error;

      // Never retry cancellations or deterministic billing failures.
      if (isAbortError(error) || isBillingError(error)) throw error;

      // Non-idempotent op that actually landed despite the error? Recover it.
      if (verifySucceeded) {
        let confirmed = false;
        try {
          confirmed = await verifySucceeded();
        } catch {
          confirmed = false;
        }
        if (confirmed) return undefined as T;
      }

      // Won't improve with more attempts — stop now.
      if (isDeterministicFailure?.(error)) throw error;
      if (attempt === maxRetries) throw error;

      const delay = Math.min(
        maxDelayMs,
        initialDelayMs * Math.pow(UPLOAD_REQUEST_BACKOFF_MULTIPLIER, attempt),
      );
      onRetry?.(attempt + 1, delay, toError(error));
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  throw lastError;
}
